import { DetailFields, DetailSection, LoadFailure } from '../../../core/index.js'
import { FeedbackCategoryBadge, FeedbackStatusBadge, formatWhen, submitterName } from '../../components/FeedbackStatus.js'
import type { ChangeRequestStatus, ChangeRequestView, FeedbackItem } from '../../types.js'
import { actorLabel, CHANGE_EVENT_LABEL, CHANGE_STATUS_CLASS, CHANGE_STATUS_LABEL } from './changeText.js'

export function ChangeStatusChip({ status }: { status: ChangeRequestStatus }) {
  return <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 text-[10px] font-bold border rounded ${CHANGE_STATUS_CLASS[status]}`}>{CHANGE_STATUS_LABEL[status]}</span>
}

/** Every step the change has taken, oldest first, with who took it and what it carried. */
export function ChangeTimeline({ change }: { change: ChangeRequestView }) {
  return (
    <DetailSection title="Timeline">
      <ol className="relative ml-1.5 border-l border-marine/20">
        {change.events.map((event, index) => (
          <li key={`${event.at}-${index}`} className="relative pb-2.5 pl-4 last:pb-0">
            <span aria-hidden="true" className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-marine" />
            <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="font-bold text-ink">{CHANGE_EVENT_LABEL[event.kind]}</span>
              <span className="text-ink-mid">
                {actorLabel(event.actor)}
                {event.actor.role === 'root' && ' (root)'} · {formatWhen(event.at)}
              </span>
              <span className="ml-auto">
                <ChangeStatusChip status={event.to} />
              </span>
            </div>
            {event.note && <p className="mt-0.5 whitespace-pre-wrap break-words text-[11px] text-ink-mid">{event.note}</p>}
          </li>
        ))}
      </ol>
    </DetailSection>
  )
}

interface LinkedFeedbackProps {
  feedbackIds: readonly string[]
  items: readonly FeedbackItem[] | undefined
  isError: boolean
  isFetching: boolean
  onRetry: () => void
}

/** The feedback the change answers, read-only: the comment in the asker's words, who asked and the screen they were on. */
export function LinkedFeedback({ feedbackIds, items, isError, isFetching, onRetry }: LinkedFeedbackProps) {
  const byId = new Map((items ?? []).map((item) => [item.feedbackId, item]))
  return (
    <DetailSection title={`Linked feedback · ${feedbackIds.length}`} endpoint="GET /admin/feedback">
      {isError ? (
        <LoadFailure size="xs" message="Could not load the feedback." onRetry={onRetry} retrying={isFetching} />
      ) : (
        <ul className="flex flex-col gap-2">
          {feedbackIds.map((feedbackId) => {
            const item = byId.get(feedbackId)
            return (
              <li key={feedbackId} className="rounded border border-marine/10 bg-canvas p-2.5">
                {item ? (
                  <>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-xs font-extrabold text-ink">{submitterName(item.submittedBy)}</span>
                      {item.submittedBy.organizationName && <span className="text-[11px] text-ink-mid">{item.submittedBy.organizationName}</span>}
                      <span className="ml-auto text-[11px] text-ink-mid">{formatWhen(item.submittedAt)}</span>
                      <FeedbackCategoryBadge category={item.category} />
                      <FeedbackStatusBadge status={item.status} />
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs text-ink">{item.comment}</p>
                    {item.screen && (
                      <p className="mt-1 text-[11px] text-ink-mid">
                        Screen: {item.screen.title || item.screen.path} <span className="font-mono">{item.screen.path}</span>
                      </p>
                    )}
                  </>
                ) : (
                  <span className="font-mono text-[11px] text-ink-mid">{items ? `${feedbackId} (no longer listed)` : `${feedbackId} · loading…`}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </DetailSection>
  )
}

/** How a closed change ended: accepted at a commit, or rejected or cancelled with a reason. */
export function ChangeOutcome({ change }: { change: ChangeRequestView }) {
  if (change.acceptance) {
    return (
      <DetailSection title="Accepted">
        <DetailFields
          fields={[
            { label: 'Commit', value: change.acceptance.headSha, mono: true },
            { label: 'By', value: change.acceptance.byName ?? change.acceptance.by },
            { label: 'When', value: formatWhen(change.acceptance.at) },
            ...(change.pullRequest ? [{ label: 'Pull request', value: <a href={change.pullRequest.url} target="_blank" rel="noreferrer" className="text-marine hover:text-marine-dk break-all">{change.pullRequest.url}</a> }] : []),
          ]}
        />
      </DetailSection>
    )
  }
  if (change.closure) {
    return (
      <DetailSection title={change.closure.outcome === 'rejected' ? 'Rejected' : 'Cancelled'}>
        <DetailFields
          fields={[
            { label: 'Reason', value: <span className="whitespace-pre-wrap">{change.closure.reason}</span> },
            { label: 'By', value: change.closure.byName ?? change.closure.by },
            { label: 'When', value: formatWhen(change.closure.at) },
          ]}
        />
      </DetailSection>
    )
  }
  return null
}
